import { Routes, Route, Link, useLocation, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState, Suspense, lazy } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import { authService } from './services/authService';
import { PreferenciasProvider } from './contexts/PreferenciasContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { ForCargo } from './components/CanAccess';
import MobileMenuButton from './components/MobileMenuButton';
import NotificationBell from './components/NotificationBell';
import SidebarUserPhoto from './components/SidebarUserPhoto';
import { NavLink } from 'react-router-dom';
import SistemaNotificacoes from './components/SistemaNotificacoes';
import AlterarSenha from './components/AlterarSenha';
import SupportChat from './components/SupportChat';
import VisualChecklist from './components/VisualChecklist';
import { AdminAuthProvider, ProtectedAdminRoute } from './contexts/AdminAuthContext';
import { AdminLayout } from './components/admin/AdminLayout';
import { TrialBanner } from './components/TrialBanner';
import { API_CONFIG } from './services/staticDataConfig';
import MenuGuard from './components/MenuGuard';
import { usePermission } from './hooks/usePermission';

interface AssinaturaResumo {
  status_assinatura: string;
  dias_restantes: number;
}

function lazyWithRefresh<T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  cacheKey: string
) {
  return lazy(async () => {
    const storageKey = `lazy-refresh:${cacheKey}`;

    try {
      const module = await importer();
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      const hasRefreshed = sessionStorage.getItem(storageKey) === '1';
      if (!hasRefreshed) {
        sessionStorage.setItem(storageKey, '1');
        window.location.reload();
      }
      throw error;
    }
  });
}

// Rotas com code-splitting para reduzir JS inicial na landing
const Login = lazy(() => import('./pages/Login'));
const Cadastro = lazy(() => import('./pages/Cadastro'));
const CadastroEstudante = lazy(() => import('./pages/CadastroEstudante'));
const CadastroPsicologo = lazy(() => import('./pages/cadastro/CadastroPsicologo'));
const CadastroClinica = lazy(() => import('./pages/cadastro/CadastroClinica'));
const CadastroEstudanteNovo = lazy(() => import('./pages/cadastro/CadastroEstudante'));
const SelecionarPerfil = lazy(() => import('./pages/cadastro/SelecionarPerfil'));
const Onboarding = lazyWithRefresh(() => import('./pages/Onboarding'), 'onboarding');
const EsqueciSenha = lazy(() => import('./pages/EsqueciSenha'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const EbooksPage = lazy(() => import('./pages/EbooksPage'));
const ListaPacientes = lazy(() => import('./pages/ListaPacientes'));
const FormPaciente = lazy(() => import('./pages/FormPaciente'));
const DetalhesPaciente = lazy(() => import('./pages/DetalhesPaciente'));
const ListaConsultas = lazy(() => import('./pages/ListaConsultas'));
const FormConsulta = lazy(() => import('./pages/FormConsulta'));
const Agenda = lazy(() => import('./pages/Agenda'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProntuarioConsulta = lazy(() => import('./pages/ProntuarioConsulta'));
const ListaUsuarios = lazy(() => import('./pages/ListaUsuarios'));
const FormUsuario = lazy(() => import('./pages/FormUsuario'));
const ImportExportPage = lazy(() => import('./pages/ImportExportPage'));
const PersonalizarPage = lazy(() => import('./pages/PersonalizarPage'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const TodoList = lazy(() => import('./pages/TodoList'));
const Objetivos = lazy(() => import('./pages/Objetivos'));
const PoliticaSeguranca = lazy(() => import('./pages/PoliticaSeguranca'));
const SophyGames = lazy(() => import('./pages/SophyGames'));
const SophyCacaPalavras = lazy(() => import('./pages/SophyCacaPalavras'));
const SophyPalavrasCruzadas = lazy(() => import('./pages/SophyPalavrasCruzadas'));
const Sophyteca = lazy(() => import('./pages/Sophyteca'));
const SophytecaFavoritos = lazy(() => import('./pages/SophytecaFavoritos'));
const SophytecaAdmin = lazy(() => import('./pages/SophytecaAdmin'));
const LogsAuditoria = lazy(() => import('./pages/LogsAuditoria'));
const ControleMenus = lazy(() => import('./pages/ControleMenus'));
const AnaliseGeral = lazy(() => import('./pages/AnaliseGeral'));
const AlertasIA = lazy(() => import('./pages/AlertasIA'));
const MeuSite = lazy(() => import('./pages/MeuSite'));
const PagamentoPage = lazy(() => import('./pages/Pagamento').then(m => ({ default: m.PagamentoPage })));
const AnamnesePublica = lazy(() => import('./pages/AnamnesePublica').then(m => ({ default: m.AnamnesePublica })));
const AgendamentoPublico = lazy(() => import('./pages/AgendamentoPublico'));
const AnamneseTemplates = lazy(() => import('./pages/AnamneseTemplates'));
const AnamneseEditor = lazy(() => import('./pages/AnamneseEditor'));
const AnamneseseLinkManager = lazy(() => import('./pages/AnamneseseLinkManager'));
const AnamnesePreenchimento = lazy(() => import('./pages/AnamnesePreenchimento'));
const NotFound = lazy(() => import('./pages/NotFound'));
const LoginAdmin = lazy(() => import('./pages/admin').then(m => ({ default: m.LoginAdmin })));
const AdminDashboard = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDashboard })));
const AdminEmpresas = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminEmpresas })));
const AdminUsuarios = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminUsuarios })));
const AdminAuditoria = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminAuditoria })));
const AdminComunicacao = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminComunicacao })));
const AdminMonitoringReport = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminMonitoringReport })));
const AdminPlanos = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminPlanos })));
const AdminLeads = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminLeads })));
const ListaPesquisas = lazy(() => import('./pages/admin/pesquisas').then(m => ({ default: m.ListaPesquisas })));
const EditorPesquisa = lazy(() => import('./pages/admin/pesquisas').then(m => ({ default: m.EditorPesquisa })));
const EstatisticasPesquisa = lazy(() => import('./pages/admin/pesquisas').then(m => ({ default: m.EstatisticasPesquisa })));
const RespostasPesquisa = lazy(() => import('./pages/admin/pesquisas').then(m => ({ default: m.RespostasPesquisa })));
const RespostaPesquisa = lazy(() => import('./pages/public/RespostaPesquisa').then(m => ({ default: m.RespostaPesquisa })));
const ConfiguracaoIA = lazy(() => import('./pages/admin/ConfiguracaoIA'));
// Removido: AutoRelatorios (sem plano na documentação)
// Removido: SuporteTickets (redundante com botão flutuante SupportChat)
// Removido: ConcorrentesPage (será implementado no painel administrativo)
const ListaDocumentos = lazy(() => import('./pages/ListaDocumentos'));
const GerarDocumento = lazy(() => import('./pages/GerarDocumento'));

const SuspenseFallback = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="text-center">
      <i className="fas fa-spinner fa-spin fa-2x mb-3" aria-hidden="true"></i>
      <p className="mb-0">Carregando...</p>
    </div>
  </div>
);

// ✅ Componente de redirecionamento para sites públicos
const RedirectToPublicSite = () => {
  const { slug } = useParams<{ slug: string }>();
  
  useEffect(() => {
    if (slug) {
      // Forçar navegação externa (sai do React Router)
      window.location.href = `/sites/${slug}`;
    }
  }, [slug]);
  
  return (
    <div className="content-wrapper">
      <section className="content">
        <div className="container-fluid text-center mt-5">
          <i className="fas fa-spinner fa-spin fa-3x mb-3"></i>
          <p>Redirecionando para o site...</p>
        </div>
      </section>
    </div>
  );
};

// ============================================================
// AppContent: sub-componente que usa usePermission()
// DEVE ficar DENTRO do <PermissionProvider> para que o hook
// consiga acessar o contexto corretamente.
// ============================================================
function AppContent() {
  const location = useLocation();
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);
  // Controle explícito do estado de expansão do menu SophyGames
  const [sophyGamesOpen, setSophyGamesOpen] = useState<boolean>(false);
  // ✅ Estado do dropdown do usuário (React puro - não depende de jQuery)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [assinaturaResumo, setAssinaturaResumo] = useState<AssinaturaResumo | null>(null);
  
  // ✅ CORREÇÃO: usePermission() chamado DENTRO do PermissionProvider (via AppContent)
  const usuarioAtual = authService.getUsuario();
  const isAtendente = usuarioAtual?.cargo === 'atendente';
  const { menuVisivel, isAdmin } = usePermission();
  
  // IMPORTANTE: Declarar isPublicRoute ANTES de usar nos useEffect
  const isAuthenticated = authService.isAuthenticated();

  useEffect(() => {
    const fetchAssinaturaResumo = async () => {
      try {
        if (!authService.isAuthenticated()) {
          setAssinaturaResumo(null);
          return;
        }

        const response = await fetch(`${API_CONFIG.BASE_URL}/empresas/minha-assinatura`, {
          headers: {
            Authorization: `Bearer ${authService.getToken()}`
          }
        });

        const result = await response.json();
        if (result?.sucesso && result?.dados) {
          setAssinaturaResumo(result.dados);
        }
      } catch (error) {
        console.error('[APP] Erro ao buscar resumo da assinatura:', error);
      }
    };

    fetchAssinaturaResumo();
  }, [isAuthenticated]);
  
  // Lista de rotas protegidas conhecidas
  const protectedRoutes = [
    '/dashboard', '/pacientes', '/consultas', '/agenda', '/relatorios',
    '/analise-geral', '/todo-list', '/objetivos', '/sophygames', '/sophyteca',
    '/usuarios', '/import-export', '/personalizar', '/configuracoes',
    '/pagamento', '/logs-auditoria', '/visual-checklist', '/documentos',
    '/onboarding', '/controle-menus'
  ];
  
  // Lista de rotas públicas conhecidas
  const publicRoutes = [
    '/login', '/cadastro', '/cadastro-estudante',
    '/cadastro/psicologo', '/cadastro/clinica', '/cadastro/estudante', '/cadastro/selecionar',
    '/esqueci-senha', '/forgot-password', '/reset-password',
    '/politica-seguranca',
    '/login-admin', // ✅ Login da área administrativa
    '/ebooks', // ✅ Página pública de e-books
    '/prontuario-eletronico', // ✅ Página de recurso SEO
    '/agenda-online-psicologia', // ✅ Página de recurso SEO
    '/ia-clinica-psicologia', // ✅ Página de recurso SEO
  ];
  
  // Verificar se é rota da área administrativa
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login-admin';
  
  // Verificar se é rota de pesquisa pública
  const isPesquisaPublica = location.pathname.startsWith('/pesquisa/');
  
  // Verificar se é rota de anamnese pública (apenas /anamnese/:token - 64 caracteres hex)
  // NÃO incluir /anamnese/preencher (rota protegida do psicólogo)
  const isAnamnesePublica = /^\/anamnese\/[a-f0-9]{64}$/i.test(location.pathname);
  
  // Verificar se é rota de agendamento público
  const isAgendamentoPublico = location.pathname.startsWith('/agendar/');
  
  // Verificar se é rota sempre pública (independente de autenticação)
  const isAlwaysPublicRoute = location.pathname === '/ebooks' ||
                               location.pathname.startsWith('/ebooks') ||
                               location.pathname === '/prontuario-eletronico' ||
                               location.pathname === '/agenda-online-psicologia' ||
                               location.pathname === '/ia-clinica-psicologia';

  // Determinar se a rota atual é pública
  const isKnownPublicRoute = publicRoutes.includes(location.pathname) || 
                              isAlwaysPublicRoute ||
                              (location.pathname === '/' && !isAuthenticated) ||
                              isPesquisaPublica ||
                              isAnamnesePublica ||
                              isAgendamentoPublico;
  const isKnownProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
  
  // Se não está logado e não é uma rota protegida conhecida, trata como público (para mostrar 404 público)
  const isPublicRoute = isKnownPublicRoute || (!isAuthenticated && !isKnownProtectedRoute);
  
  // Diagnóstico SIMPLIFICADO para nova arquitetura
  useEffect(() => {
//     console.log('[APP] Inicialização do App com novo sistema cross-tab', {
//       url: window.location.href,
//       pathname: location.pathname,
//       isPublicRoute,
//       sessaoInfo: crossTabSession.getSessionInfo()
//     });
  }, [location.pathname, isPublicRoute]);

  // Aguardar inicialização do authService
  useEffect(() => {
    authService.waitForInitialization().then(() => {
//       console.log('[APP] AuthService inicializado');
    });
  }, []);

  // ✅ AdminLTE CSS/JS já carregados via index.html (defer/media query)
  // Este useEffect ativa o CSS e inicializa quando entrar em área logada
  useEffect(() => {
    if (!isPublicRoute) {
      // Ativar AdminLTE CSS (pode estar com media=print se veio da landing)
      const adminLteCss = document.getElementById('adminlte-css') as HTMLLinkElement;
      const sourceSansFont = document.getElementById('source-sans-font') as HTMLLinkElement;
      const bootstrapIcons = document.getElementById('bootstrap-icons-css') as HTMLLinkElement;
      
      if (adminLteCss) adminLteCss.media = 'all';
      if (sourceSansFont) sourceSansFont.media = 'all';
      if (bootstrapIcons) bootstrapIcons.media = 'all';
      
      // Função para inicializar AdminLTE quando disponível
      const inicializarAdminLTE = () => {
        if (window.jQuery && (window as any).AdminLTE) {
          try {
            // Inicializar treeview
            window.jQuery('[data-widget="treeview"]').each(function() {
              (window as any).AdminLTE.Treeview._jQueryInterface.call(window.jQuery(this), 'init');
            });
            console.log('[APP] AdminLTE inicializado');
          } catch (error) {
            console.error('[APP] Erro ao inicializar AdminLTE:', error);
          }
        }
      };
      
      // Tentar inicializar imediatamente e após delays
      inicializarAdminLTE();
      const timers = [100, 300, 500, 1000].map(delay => 
        setTimeout(inicializarAdminLTE, delay)
      );
      
      return () => timers.forEach(clearTimeout);
    }
  }, [isPublicRoute]);

  // ✅ Fechar dropdown do usuário ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('user-dropdown-container');
      if (dropdown && !dropdown.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    
    if (userDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [userDropdownOpen]);

  // Inicializar AdminLTE treeview após login para garantir que submenus funcionem
  useEffect(() => {
    if (isAuthenticated && !isPublicRoute) {
      // Função para expandir menus
      const expandActiveMenu = () => {
        // Buscar todos os itens com submenu (has-treeview)
        const treeviewItems = document.querySelectorAll('.nav-item.has-treeview');
        
        treeviewItems.forEach((item: any) => {
          // Verificar se algum link dentro do submenu está ativo
          const activeLink = item.querySelector('.nav-treeview .nav-link.active');
          
          if (activeLink) {
            // Adicionar classe menu-open no item pai para expandir
            item.classList.add('menu-open');
            
            // Adicionar classe active no link pai (âncora do submenu)
            const parentLink = item.querySelector(':scope > .nav-link');
            if (parentLink) {
              parentLink.classList.add('active');
            }
          } else {
            // Remover classes se não há link ativo (para colapsar outros menus)
            item.classList.remove('menu-open');
            const parentLink = item.querySelector(':scope > .nav-link');
            if (parentLink) {
              parentLink.classList.remove('active');
            }
          }
        });
      };
      
  // Executar imediatamente
  expandActiveMenu();
  // Abrir/fechar SophyGames com base na rota atual
  setSophyGamesOpen(location.pathname.startsWith('/sophygames'));
      
      // Executar novamente após um pequeno delay (garante que o DOM está pronto)
      const timer1 = setTimeout(expandActiveMenu, 100);
      
      // Executar mais uma vez para garantir (para casos de renderização lenta)
      const timer2 = setTimeout(expandActiveMenu, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isAuthenticated, isPublicRoute, location.pathname]);

  return (
    <>
        <div className="wrapper">
          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        
        {/* Modal de alterar senha */}
        {showAlterarSenha && (
          <AlterarSenha onClose={() => setShowAlterarSenha(false)} />
        )}

        <Suspense fallback={<SuspenseFallback />}>
          {/* ✅ ÁREA ADMINISTRATIVA - Sistema separado */}
          {isAdminRoute ? (
            <AdminAuthProvider>
              <Routes>
                {/* Login Admin */}
                <Route path="/login-admin" element={<LoginAdmin />} />
                
                {/* Rotas protegidas do Admin */}
                <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="empresas" element={<AdminEmpresas />} />
                  <Route path="usuarios" element={<AdminUsuarios />} />
                  <Route path="auditoria" element={<AdminAuditoria />} />
                  <Route path="comunicacao" element={<AdminComunicacao />} />
                  <Route path="monitoramento" element={<AdminMonitoringReport />} />
                  <Route path="planos" element={<AdminPlanos />} />
                  <Route path="leads" element={<AdminLeads />} />
                  {/* Pesquisas/Formulários */}
                  <Route path="pesquisas" element={<ListaPesquisas />} />
                  <Route path="pesquisas/nova" element={<EditorPesquisa />} />
                  <Route path="pesquisas/:id/editar" element={<EditorPesquisa />} />
                  <Route path="pesquisas/:id/estatisticas" element={<EstatisticasPesquisa />} />
                  <Route path="pesquisas/:id/respostas" element={<RespostasPesquisa />} />
                  {/* Rotas futuras */}
                  <Route path="administradores" element={<div className="text-center py-5"><h3>Em desenvolvimento</h3><p className="text-muted">Gerenciamento de administradores do sistema</p></div>} />
                  <Route path="configuracoes" element={<div className="text-center py-5"><h3>Em desenvolvimento</h3><p className="text-muted">Configurações do sistema</p></div>} />
                  {/* Configuração de IA */}
                  <Route path="ia" element={<ConfiguracaoIA />} />
                </Route>
                
                {/* 404 para rotas admin não encontradas */}
                <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </AdminAuthProvider>
          ) : isPublicRoute ? (
            // Rotas públicas (landing, login, cadastro, etc.)
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<SelecionarPerfil />} />
              <Route path="/cadastro/selecionar" element={<SelecionarPerfil />} />
              <Route path="/cadastro/legado" element={<Cadastro />} />
              <Route path="/cadastro-estudante" element={<CadastroEstudante />} />
              {/* ✅ Novos fluxos de cadastro separados por tipo */}
              <Route path="/cadastro/psicologo" element={<CadastroPsicologo />} />
              <Route path="/cadastro/clinica" element={<CadastroClinica />} />
              <Route path="/cadastro/estudante" element={<CadastroEstudanteNovo />} />
              <Route path="/esqueci-senha" element={<EsqueciSenha />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/politica-seguranca" element={<PoliticaSeguranca />} />
              {/* Páginas de E-books */}
              <Route path="/ebooks" element={<EbooksPage />} />
              {/* Páginas estáticas de recursos SEO — Nginx serve o HTML diretamente;
                  estas entradas garantem que o React Router não as trate como 404 */}
              <Route path="/prontuario-eletronico" element={null} />
              <Route path="/agenda-online-psicologia" element={null} />
              <Route path="/ia-clinica-psicologia" element={null} />
              {/* Pesquisa pública - responder formulários */}
              <Route path="/pesquisa/:linkPublico" element={<RespostaPesquisa />} />
              {/* Anamnese pública - preenchimento por pacientes */}
              <Route path="/anamnese/:token" element={<AnamnesePublica />} />
              {/* Agendamento online público - pacientes agendam consultas */}
              <Route path="/agendar/:token" element={<AgendamentoPublico />} />
              {/* Rota catch-all para 404 (rotas públicas) */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          ) : (
            // Layout completo para rotas protegidas
            <>
              {/* Sistema de notificações - APENAS na área logada */}
            <SistemaNotificacoes />
            
            <aside className="main-sidebar sidebar-dark-primary elevation-4">
              <Link to={isAtendente ? '/consultas' : '/dashboard'} className="brand-link">
                <img 
                  src="/img/logo-icone.png" 
                  alt="Logo Sophya" 
                  className="brand-image img-circle elevation-3" 
                  style={{ opacity: 0.8 }} 
                />
                <span className="brand-text font-weight-light">Sophya</span>
              </Link>

            <div className="sidebar">
              <div className="user-panel mt-3 pb-3 mb-3 d-flex">
                <div className="image">
                  <SidebarUserPhoto />
                </div>
                <div className="info">
                  <Link to={isAtendente ? '/consultas' : '/dashboard'} className="d-block">
                    {usuarioAtual ? usuarioAtual.nome : 'Usuário'}
                  </Link>
                </div>
              </div>

              <nav className="mt-2">
                <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">
                  <MenuGuard slug="dashboard">
                    <li className="nav-item">
                      <Link to="/dashboard" className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-tachometer-alt"></i>
                        <p>Dashboard</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  <MenuGuard slug="pacientes">
                    <li className="nav-item">
                      <Link to="/pacientes" className={`nav-link ${location.pathname.startsWith('/pacientes') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-users"></i>
                        <p>Pacientes</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  <MenuGuard slug="consultas">
                    <li className="nav-item">
                      <Link to="/consultas" className={`nav-link ${location.pathname.startsWith('/consultas') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-calendar-check"></i>
                        <p>Consultas</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  <MenuGuard slug="agenda">
                    <li className="nav-item">
                      <Link to="/agenda" className={`nav-link ${location.pathname === '/agenda' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-calendar"></i>
                        <p>Agenda</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  <MenuGuard slug="relatorios">
                    <li className="nav-item">
                      <Link to="/relatorios" className={`nav-link ${location.pathname === '/relatorios' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-chart-bar"></i>
                        <p>Relatórios</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  <MenuGuard slug="analise-geral">
                    <li className="nav-item">
                      <Link to="/analise-geral" className={`nav-link ${location.pathname === '/analise-geral' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-brain"></i>
                        <p>Análise Geral (IA)</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="alertas-ia">
                    <li className="nav-item">
                      <Link to="/alertas-ia" className={`nav-link ${location.pathname === '/alertas-ia' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-robot"></i>
                        <p>Alertas Inteligentes</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="documentos">
                    <li className="nav-item">
                      <Link to="/documentos" className={`nav-link ${location.pathname.startsWith('/documentos') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-file-medical"></i>
                        <p>Documentos</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  {/* Menu Anamnese (pai controla filhos) */}
                  <MenuGuard slug="anamnese">
                    <li className="nav-item">
                      <Link to="/anamnese/templates" className={`nav-link ${location.pathname.startsWith('/anamnese') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-clipboard-list"></i>
                        <p>
                          Anamnese
                          <i className="fas fa-angle-left right"></i>
                        </p>
                      </Link>
                      <ul className={`nav nav-treeview ${location.pathname.startsWith('/anamnese') ? 'menu-open' : ''}`}>
                        <li className="nav-item">
                          <Link to="/anamnese/templates" className={`nav-link ${location.pathname === '/anamnese/templates' ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon"></i>
                            <p>Templates</p>
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link to="/anamnese/editor" className={`nav-link ${location.pathname === '/anamnese/editor' ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon"></i>
                            <p>Criar Formulário</p>
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link to="/anamnese/links" className={`nav-link ${location.pathname === '/anamnese/links' ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon"></i>
                            <p>Gerenciar Links</p>
                          </Link>
                        </li>
                      </ul>
                    </li>
                  </MenuGuard>
                                    
                  <MenuGuard slug="todo-list">
                    <li className="nav-item">
                      <Link to="/todo-list" className={`nav-link ${location.pathname === '/todo-list' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-tasks"></i>
                        <p>Lista de Tarefas</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="objetivos">
                    <li className="nav-item">
                      <Link to="/objetivos" className={`nav-link ${location.pathname.startsWith('/objetivos') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-bullseye"></i>
                        <p>Meus Objetivos</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  {/* Menu SophyGames (pai controla filhos) */}
                  <MenuGuard slug="sophygames">
                    <li 
                      id="sophygames-menu"
                      className={`nav-item has-treeview ${sophyGamesOpen ? 'menu-open' : ''}`}
                    >
                      <a 
                        href="#" 
                        className={`nav-link ${sophyGamesOpen ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setSophyGamesOpen((prev) => !prev);
                        }}
                      >
                        <i className="nav-icon fas fa-gamepad"></i>
                        <p>
                          SophyGames 🎮
                          <i className="right fas fa-angle-left"></i>
                        </p>
                      </a>
                      <ul className="nav nav-treeview" style={{ display: sophyGamesOpen ? 'block' : 'none' }}>
                        <li className="nav-item">
                          <Link to="/sophygames" className={`nav-link ${location.pathname === '/sophygames' ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon"></i>
                            <p>Flashcards</p>
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link to="/sophygames/cacapalavras" className={`nav-link ${location.pathname === '/sophygames/cacapalavras' ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon"></i>
                            <p>Caça-Palavras</p>
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link to="/sophygames/palavrascruzadas" className={`nav-link ${location.pathname === '/sophygames/palavrascruzadas' ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon"></i>
                            <p>Palavras Cruzadas</p>
                          </Link>
                        </li>
                      </ul>
                    </li>
                  </MenuGuard>
                  
                  <MenuGuard slug="sophyteca">
                    <li className="nav-item">
                      <Link to="/sophyteca" className={`nav-link ${location.pathname.startsWith('/sophyteca') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-book-reader"></i>
                        <p>Sophyteca 📚</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  {/* Menu Administrativo */}
                  {(menuVisivel('usuarios') || menuVisivel('import-export') || menuVisivel('logs-auditoria') || (isAdmin && menuVisivel('controle-menus'))) && (
                    <li className="nav-header">ADMINISTRAÇÃO</li>
                  )}

                  <MenuGuard slug="usuarios">
                    <li className="nav-item">
                      <Link to="/usuarios" className={`nav-link ${location.pathname.startsWith('/usuarios') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-user-cog"></i>
                        <p>Usuários</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="import-export">
                    <li className="nav-item">
                      <Link to="/import-export" className={`nav-link ${location.pathname === '/import-export' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-exchange-alt"></i>
                        <p>Import/Export</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="logs-auditoria">
                    <li className="nav-item">
                      <Link to="/logs-auditoria" className={`nav-link ${location.pathname === '/logs-auditoria' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-clipboard-check"></i>
                        <p>Auditoria</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  {isAdmin && (
                    <MenuGuard slug="controle-menus">
                      <li className="nav-item">
                        <Link to="/controle-menus" className={`nav-link ${location.pathname === '/controle-menus' ? 'active' : ''}`}>
                          <i className="nav-icon fas fa-sliders-h"></i>
                          <p>Controle de Menus</p>
                        </Link>
                      </li>
                    </MenuGuard>
                  )}

                  {/* Configurações */}
                  {(menuVisivel('personalizar') || menuVisivel('configuracoes') || menuVisivel('meu-site')) && (
                    <li className="nav-header">CONFIGURAÇÕES</li>
                  )}

                  <MenuGuard slug="personalizar">
                    <li className="nav-item">
                      <Link to="/personalizar" className={`nav-link ${location.pathname === '/personalizar' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-palette"></i>
                        <p>Personalizar</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="configuracoes">
                    <li className="nav-item">
                      <Link to="/configuracoes" className={`nav-link ${location.pathname === '/configuracoes' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-cogs"></i>
                        <p>Configurações</p>
                      </Link>
                    </li>
                  </MenuGuard>

                  <MenuGuard slug="meu-site">
                    <li className="nav-item">
                      <Link to="/meu-site" className={`nav-link ${location.pathname === '/meu-site' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-globe"></i>
                        <p>Meu Site</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  {/* Pagamento */}
                  <MenuGuard slug="pagamento">
                    <li className="nav-item">
                      <Link to="/pagamento" className="nav-link">
                        <i className="nav-icon fas fa-credit-card"></i>
                        <p>Pagamento</p>
                      </Link>
                    </li>
                  </MenuGuard>
                  
                  <li className="nav-item">
                    <a 
                      href="#" 
                      className="nav-link"
                      onClick={(e) => {
                        e.preventDefault();
                        if (confirm('Tem certeza que deseja sair?')) {
                          authService.logout();
                        }
                      }}
                    >
                      <i className="nav-icon fas fa-sign-out-alt"></i>
                      <p>Sair</p>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </aside>

          <nav className="main-header navbar navbar-expand navbar-white navbar-light">
            <ul className="navbar-nav">
              <li className="nav-item">
                <a className="nav-link" data-widget="pushmenu" href="#" role="button">
                  <i className="fas fa-bars"></i>
                </a>
              </li>
            </ul>

            <ul className="navbar-nav ml-auto">
              {/* Sino de notificações */}
              <NotificationBell />
              
              {/* ✅ Dropdown do usuário - React puro (não depende de jQuery) */}
              <li className="nav-item dropdown" id="user-dropdown-container">
                <a 
                  className="nav-link" 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setUserDropdownOpen(!userDropdownOpen);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="far fa-user"></i>
                  <span className="ml-2">{usuarioAtual ? usuarioAtual.nome : 'Usuário'}</span>
                  <i className={`fas fa-chevron-${userDropdownOpen ? 'up' : 'down'} ml-1`} style={{ fontSize: '0.7rem' }}></i>
                </a>
                <div 
                  className={`dropdown-menu dropdown-menu-lg dropdown-menu-right ${userDropdownOpen ? 'show' : ''}`}
                  style={{ 
                    display: userDropdownOpen ? 'block' : 'none',
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    zIndex: 1050
                  }}
                >
                  {assinaturaResumo && (
                    <>
                      <div className="dropdown-item text-wrap" style={{ whiteSpace: 'normal' }}>
                        <i className="fas fa-check-circle text-primary mr-2"></i>
                        <strong>Plano {assinaturaResumo.status_assinatura}</strong>
                        <div className="text-muted small mt-1">
                          Restam {Math.max(0, assinaturaResumo.dias_restantes)} dia(s) de plano
                        </div>
                      </div>
                      <div className="dropdown-divider"></div>
                      <Link
                        to="/pagamento"
                        className="dropdown-item"
                        onClick={() => setUserDropdownOpen(false)}
                      >
                        <i className="fas fa-credit-card mr-2"></i> Ir para Pagamento
                      </Link>
                      <div className="dropdown-divider"></div>
                    </>
                  )}
                  <a 
                    href="#" 
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      setUserDropdownOpen(false);
                      setShowAlterarSenha(true);
                    }}
                  >
                    <i className="fas fa-key mr-2"></i> Alterar Senha
                  </a>
                  <div className="dropdown-divider"></div>
                  <a 
                    href="#" 
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      setUserDropdownOpen(false);
                      if (confirm('Tem certeza que deseja sair?')) {
                        authService.logout();
                      }
                    }}
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i> Sair
                  </a>
                </div>
              </li>
            </ul>
          </nav>

          {/* Overlay para sidebar em mobile */}
          <div className="sidebar-overlay d-md-none" />

          {/* Botão flutuante de menu apenas em mobile (opcional) */}
          <MobileMenuButton />

          <div className="content-wrapper">
            {/* ✅ Banner global de Trial / Soft Block */}
            <TrialBanner />
            <Routes>
              {/* Rotas protegidas */}
              <Route path="/" element={<ProtectedRoute><Navigate to={isAtendente ? '/consultas' : '/dashboard'} replace /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Dashboard />}</ProtectedRoute>} />
              <Route path="/objetivos" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Objetivos />}</ProtectedRoute>} />
              
              {/* Pacientes */}
              <Route path="/pacientes" element={<ProtectedRoute><ListaPacientes /></ProtectedRoute>} />
              <Route path="/pacientes/novo" element={<ProtectedRoute><FormPaciente /></ProtectedRoute>} />
              <Route path="/pacientes/:id/editar" element={<ProtectedRoute><FormPaciente /></ProtectedRoute>} />
              <Route path="/pacientes/:id" element={<ProtectedRoute><DetalhesPaciente /></ProtectedRoute>} />
              <Route path="/pacientes/:id/prontuario" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <ProntuarioConsulta />}</ProtectedRoute>} />
              
              {/* Consultas */}
              <Route path="/consultas" element={<ProtectedRoute><ListaConsultas /></ProtectedRoute>} />
              <Route path="/consultas/nova" element={<ProtectedRoute><FormConsulta /></ProtectedRoute>} />
              <Route path="/consultas/:id/editar" element={<ProtectedRoute><FormConsulta /></ProtectedRoute>} />
              <Route path="/consultas/:id/prontuario" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <ProntuarioConsulta />}</ProtectedRoute>} />
              
              {/* Agenda */}
              <Route path="/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
              
              {/* Relatórios */}
              <Route path="/relatorios" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Relatorios />}</ProtectedRoute>} />
              
              {/* Removido: Relatórios Automáticos, Suporte Tickets, Concorrentes */}
              
              {/* Análise Geral de Paciente */}
              <Route path="/analise-geral" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnaliseGeral />}</ProtectedRoute>} />
              
              {/* Alertas Inteligentes IA */}
              <Route path="/alertas-ia" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AlertasIA />}</ProtectedRoute>} />
              
              {/* Documentos Profissionais */}
              <Route path="/documentos" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <ListaDocumentos />}</ProtectedRoute>} />
              <Route path="/documentos/gerar" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <GerarDocumento />}</ProtectedRoute>} />
              
              {/* To-Do List */}
              <Route path="/todo-list" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <TodoList />}</ProtectedRoute>} />
              
              {/* SophyGames */}
              <Route path="/sophygames" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophyGames />}</ProtectedRoute>} />
              <Route path="/sophygames/cacapalavras" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophyCacaPalavras />}</ProtectedRoute>} />
              <Route path="/sophygames/palavrascruzadas" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophyPalavrasCruzadas />}</ProtectedRoute>} />
              
              {/* Sophyteca - Biblioteca Digital */}
              <Route path="/sophyteca" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Sophyteca />}</ProtectedRoute>} />
              <Route path="/sophyteca/favoritos" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophytecaFavoritos />}</ProtectedRoute>} />
              <Route path="/sophyteca/admin" element={<ProtectedRoute admin><SophytecaAdmin /></ProtectedRoute>} />
              
              {/* Anamnese - Sistema de Formulários */}
              <Route path="/anamnese/templates" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnamneseTemplates />}</ProtectedRoute>} />
              <Route path="/anamnese/editor" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnamneseEditor />}</ProtectedRoute>} />
              <Route path="/anamnese/editor/:id" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnamneseEditor />}</ProtectedRoute>} />
              <Route path="/anamnese/links" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnamneseseLinkManager />}</ProtectedRoute>} />
              <Route path="/anamnese/preencher/:pacienteId/:templateId" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnamnesePreenchimento />}</ProtectedRoute>} />
              <Route path="/anamnese/preencher/:pacienteId/:templateId/:respostaId" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnamnesePreenchimento />}</ProtectedRoute>} />
              <Route path="/anamnese/:token" element={<AnamnesePublica />} />
              
              {/* Rotas administrativas */}
              {/* Lista de usuários: admin, atendente (visualização) e estudante (visualização + editar próprio) */}
              <Route path="/usuarios" element={<ProtectedRoute><ForCargo cargos={['admin','atendente','estudante']} fallback={<Navigate to="/consultas" replace /> }><ListaUsuarios /></ForCargo></ProtectedRoute>} />
              <Route path="/usuarios/novo" element={<ProtectedRoute admin><FormUsuario /></ProtectedRoute>} />
              {/* Editar usuário: admin qualquer; estudante apenas próprio (backend reforça) */}
              <Route path="/usuarios/:id/editar" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><FormUsuario /></ForCargo></ProtectedRoute>} />
              
              {/* Import/Export */}
              {/* Import/Export permitido para admin e estudante */}
              <Route path="/import-export" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><ImportExportPage /></ForCargo></ProtectedRoute>} />
              
              {/* Personalização */}
              <Route path="/personalizar" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <PersonalizarPage />}</ProtectedRoute>} />
              
              {/* Configurações */}
              <Route path="/configuracoes" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Configuracoes />}</ProtectedRoute>} />
              
              {/* Meu Site - Sites Personalizados */}
              <Route path="/meu-site" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <MeuSite />}</ProtectedRoute>} />
              
              {/* Sites Públicos - Redirecionar para fora do React Router */}
              <Route path="/sites/:slug" element={<RedirectToPublicSite />} />
              
              {/* Pagamento */}
              <Route path="/pagamento" element={<ProtectedRoute><PagamentoPage /></ProtectedRoute>} />
              {/* ✅ Onboarding pós-cadastro */}
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              {/* Auditoria */}
              <Route path="/logs-auditoria" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><LogsAuditoria /></ForCargo></ProtectedRoute>} />
              {/* Controle de Menus (RBAC Dinâmico) */}
              <Route path="/controle-menus" element={<ProtectedRoute admin><ControleMenus /></ProtectedRoute>} />
              {/* Visual Checklist (apenas admin/estudante) */}
              <Route path="/visual-checklist" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><VisualChecklist /></ForCargo></ProtectedRoute>} />
              {/* ✅ E-books - rota pública acessível também por usuários autenticados */}
              <Route path="/ebooks" element={<EbooksPage />} />
              {/* Rota catch-all para 404 (rotas protegidas) */}
              <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
            </Routes>
          </div>

          {/* Barra de navegação rápida mobile (bottom) */}
          <div className="mobile-quick-nav d-md-none">
            <NavLink to={isAtendente ? '/consultas' : '/dashboard'} className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-home" />
              <span>Início</span>
            </NavLink>
            <NavLink to="/pacientes" className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-users" />
              <span>Pacientes</span>
            </NavLink>
            <NavLink to="/consultas" className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-calendar-check" />
              <span>Consultas</span>
            </NavLink>
            <NavLink to="/agenda" className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-calendar" />
              <span>Agenda</span>
            </NavLink>
            {!isAtendente && (
              <NavLink to="/sophygames" className={({isActive}) => isActive ? 'active' : ''}>
                <span className="icon fas fa-gamepad" />
                <span>Jogos</span>
              </NavLink>
            )}
          </div>

          {/* Chat de Suporte Flutuante */}
          <SupportChat />

          <footer className="main-footer">
            <strong>Copyright &copy; 2024 <a href="#">Sophya</a>.</strong>
            Todos os direitos reservados.
            <div className="float-right d-none d-sm-inline-block">
              <b>Versão</b> 1.0.0
            </div>
          </footer>
        </>
      )}
        </Suspense>
          
          {/* Banner de Consentimento de Cookies (LGPD) */}
          <CookieConsent />
        </div>
    </>
  );
}

// ============================================================
// App: componente raiz que monta os providers
// AppContent fica DENTRO dos providers para poder usar os hooks
// ============================================================
function App() {
  return (
    <PreferenciasProvider>
      <PermissionProvider>
        <AppContent />
      </PermissionProvider>
    </PreferenciasProvider>
  );
}

export default App;
